import { useEffect, useState } from "react";
import { whenFree } from "@/lib/popupQueue";
import { useAuth } from "@/context/AuthContext";
import { levelFor, LEVEL_BONUS } from "@/lib/levels";
import { confetti, playFanfare } from "@/lib/celebrate";
import { ShareWin } from "@/components/ShareWin";
import { LevelUpShow } from "@/components/LevelUpShow";
import { useUi } from "@/lib/uiConfig";

export function LevelChip({ points, testId = "level-chip", size = "sm" }) {
  const l = levelFor(points);
  return (
    <span data-testid={testId} title={l.next ? `Noch ${l.missing} Punkte bis ${l.next.name}` : "Höchstes Level erreicht"}
      className={`inline-flex items-center gap-1 rounded-full bg-gradient-to-r ${l.color} text-white font-extrabold whitespace-nowrap shadow-sm ${size === "lg" ? "text-xs px-2.5 py-1" : "text-[10px] px-2 py-0.5"}`}>
      {l.emoji} Lv. {l.level} · {l.name}
    </span>
  );
}

export function LevelProgress({ points }) {
  const l = levelFor(points);
  return (
    <div className="space-y-1.5" data-testid="level-progress">
      <div className="flex items-center justify-between gap-x-2 gap-y-1 flex-wrap"><LevelChip points={points} size="lg" testId="level-progress-chip" />
        <span className="text-xs text-slate-500 min-w-0">{l.next ? `Noch ${l.missing} P bis ${l.next.emoji} ${l.next.name}` : "Höchstes Level! 💎"}</span></div>
      <div className="h-2 rounded-full bg-rose-100 overflow-hidden"><div className={`h-full rounded-full bg-gradient-to-r ${l.color} transition-[width] duration-700`} style={{ width: `${Math.round(l.progress * 100)}%` }} /></div>
    </div>
  );
}

export function LevelUpWatcher() {
  const { user, refreshUser } = useAuth();
  const [up, setUp] = useState(null);
  const ui = useUi();
  const points = user?.points;
  useEffect(() => {
    if (!user || points == null) return;
    const key = `itz_level_${user.user_id}`, l = levelFor(points), prev = Number(localStorage.getItem(key) || 0);
    localStorage.setItem(key, String(l.level));
    if (prev && l.level > prev) {
      whenFree(`level-${l.level}`, () => { setUp({ ...l, bonus: LEVEL_BONUS * (l.level - prev), points }); setTimeout(refreshUser, 1500); playFanfare(); confetti({ count: 160, spread: 2 }); setTimeout(() => confetti({ count: 120, spread: 2.4 }), 600); });
    }
  }, [points, user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!up) return null;
  if (ui?.levelup_v2) return <LevelUpShow up={up} onClose={() => setUp(null)} />;
  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-6 bg-slate-950/70 backdrop-blur-md wheel-fade" onClick={() => setUp(null)} data-testid="level-up-modal" style={{ pointerEvents: "auto" }}>
      <div className="relative text-center text-white levelup-pop" onClick={(e) => e.stopPropagation()}>
        <span className="burst-rays !-inset-24" aria-hidden />
        <p className="text-xs font-extrabold uppercase tracking-[0.35em] text-amber-300">Level-Aufstieg!</p>
        <div className={`mx-auto mt-4 w-36 h-36 rounded-full bg-gradient-to-br ${up.color} flex flex-col items-center justify-center levelup-medal`}>
          <span className="text-5xl leading-none">{up.emoji}</span>
          <span className="font-display text-2xl font-black mt-1" data-testid="level-up-number">Lv. {up.level}</span>
        </div>
        <h2 className="font-display text-4xl font-black mt-5 jackpot-title" data-testid="level-up-name">{up.name}</h2>
        <p className="mt-3 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-300 to-orange-400 text-amber-950 font-extrabold text-sm shadow-lg" data-testid="level-up-bonus">🎁 +{up.bonus} Bonuspunkte</p>
        <p className="mt-2 text-sm text-white/80 max-w-xs mx-auto">{up.next ? `Weiter so! Ab ${up.next.min} Punkten wirst du ${up.next.emoji} ${up.next.name}.` : "Du hast das höchste Level erreicht – unglaublich! 💎"}</p>
        <div className="mt-5"><ShareWin dark text={`Ich habe in der itzPlay Community Level ${up.level} „${up.name}“ ${up.emoji} erreicht!`} testId="level-up-share" /></div>
        <button onClick={() => setUp(null)} data-testid="level-up-close" className="mt-6 px-8 h-12 rounded-full pink-gradient font-extrabold shadow-lg shadow-rose-500/40 active:scale-[0.97] transition-transform">Yeah! 🎉</button>
      </div>
    </div>
  );
}

export function ClimberBadges({ badges = [], testId = "climber-badges" }) {
  if (!badges.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {[...badges].reverse().map((b) => (
        <span key={b.month} title={`Level-Aufsteiger des Monats ${b.label} (+${b.levels} Level)`} data-testid={`${testId}-${b.month}`}
          className="climber-badge inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold text-white bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 shadow-md shadow-fuchsia-300/50">
          🚀 Aufsteiger {b.label}
        </span>
      ))}
    </div>
  );
}
